/**
 * {{assigned_agent}} in a From / Reply-To field (mig 1550). The send path resolves it per recipient to the contact's
 * ASSIGNED support agent (never the day's coverage agent), sent as "<Agent Name> | U.S. LawShield" <agent address>;
 * when the agent can't be used (none
 * assigned, inactive, no email, or the email isn't on an authenticated sending domain) the recipient gets the
 * fallback sender, the send continues, and one alert is raised per flight / step.
 *
 * MIRRORS supabase/functions/_shared/assignedAgentSender.ts (isAssignedAgentToken). The server re-validates; this is
 * only so the form refuses anything else that looks like a variable.
 */
export const ASSIGNED_AGENT_VAR = "{{assigned_agent}}";

export function isAssignedAgentToken(v: unknown): boolean {
  return (
    typeof v === "string" && /^\{\{\s*assigned_agent\s*\}\}$/i.test(v.trim())
  );
}

// Same rule as the server (assignedAgentSender.ts isPlainEmail), so an address the form accepts is never dropped there.
const EMAIL_RE =
  /^[^\s@<>()",;:\\[\]]+@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export type ReplyToCheck =
  | { ok: true; value: string; isVariable: boolean }
  | { ok: false; error: string };

/**
 * A Reply-To field value: blank, a plain address, or exactly {{assigned_agent}} (normalised to that spelling).
 * Any other {{...}} is refused: only {{assigned_agent}} is supported here.
 */
export function checkReplyToValue(raw: string): ReplyToCheck {
  const v = raw.trim();
  if (!v) {
    return { ok: true, value: "", isVariable: false };
  }
  if (isAssignedAgentToken(v)) {
    return { ok: true, value: ASSIGNED_AGENT_VAR, isVariable: true };
  }
  if (v.includes("{{") || v.includes("}}")) {
    return {
      ok: false,
      error: `Only the ${ASSIGNED_AGENT_VAR} variable can be used here, or a normal email address.`,
    };
  }
  if (!EMAIL_RE.test(v)) {
    return {
      ok: false,
      error: `Enter a valid email address or ${ASSIGNED_AGENT_VAR}`,
    };
  }
  return { ok: true, value: v, isVariable: false };
}

/** A fallback Reply-To: blank or a plain address (never a variable). */
export function checkFallbackReplyTo(raw: string): ReplyToCheck {
  const v = raw.trim();
  if (!v) {
    return { ok: true, value: "", isVariable: false };
  }
  if (v.includes("{{") || v.includes("}}") || !EMAIL_RE.test(v)) {
    return {
      ok: false,
      error: "The fallback Reply-To must be a normal email address",
    };
  }
  return { ok: true, value: v, isVariable: false };
}

/** Plain-language help shown under the fields. */
export const ASSIGNED_AGENT_HELP =
  'Each recipient gets the email from their assigned support agent, shown as "<Agent Name> | U.S. LawShield" with the agent\'s address. ' +
  "If a recipient has no agent, the agent is inactive, or the agent's email can't be used, that recipient gets the " +
  "fallback instead. The send continues, and you (the creator) and the admins get one alert with the counts.";
